import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Wand2, ChevronRight, ChevronLeft, Music, Sparkles, Settings, Check, Library, TrendingUp, Star, AlertCircle, Plus, Minus, Save, Pencil, Trash2, RotateCcw, Loader2, Users, Zap, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from './config';

const EventWizard = ({ eventoId, onClose, onPlaylistSaved }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  // Iter fix (feb-2026): flag específico para la fase step 1→2 (LLM ~20-25s).
  // Antes se reutilizaba `loading` que mostraba "Cargando datos..." — mensaje
  // ambiguo que llevaba a usuarios a cerrar el modal creyendo que se colgó.
  const [analyzingWithAI, setAnalyzingWithAI] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Step 1: Event Profile
  const [profile, setProfile] = useState({
    evento_id: eventoId,
    nombre_evento: '',
    fecha: '',
    gustos_musicales: '',
    tipo_evento: '',
    edad_media: '30-45',
    region: 'España',
    canciones_prohibidas: '',
    canciones_imperdibles: ''
  });
  
  // Step 2: Genre Mix
  const [generosSugeridos, setGenerosSugeridos] = useState([]);
  const [explicacionIA, setExplicacionIA] = useState('');
  const [generosDisponibles, setGenerosDisponibles] = useState([]);
  const [hasManualPercentages, setHasManualPercentages] = useState(false);
  const [respetarBase, setRespetarBase] = useState(true);

  // Wizard IA — preview + apply del texto de imperdibles
  const [wizardIaLoading, setWizardIaLoading] = useState(false);
  const [wizardIaPreview, setWizardIaPreview] = useState(null); // { youtube_urls, songs, momentos_detected, stats }
  const [wizardIaApplying, setWizardIaApplying] = useState(false);
  // Refinamiento IA: sugerencias artista → aceptación individual por checkbox
  const [refineLoading, setRefineLoading] = useState(false);
  const [refineSuggestions, setRefineSuggestions] = useState([]); // [{title, suggested_artist, confidence}]
  const [refineAccepted, setRefineAccepted] = useState({}); // {title: true/false}

  const runWizardIaPreview = async () => {
    const text = (profile.canciones_imperdibles || '').trim();
    if (!text) return;
    setWizardIaLoading(true);
    try {
      const res = await axios.post(`${API_URL}/event-wizard/preview-imperdibles`, {
        text,
        playlist_id: eventoId,
      });
      setWizardIaPreview(res.data);
    } catch (err) {
      toast.error('No se pudo analizar el texto');
      console.error('[WizardIA preview] error:', err);
    } finally {
      setWizardIaLoading(false);
    }
  };

  const applyWizardIa = async () => {
    if (!wizardIaPreview) return;
    setWizardIaApplying(true);
    try {
      // Antes de aplicar, si hay sugerencias aceptadas, fusiona artista en el texto
      let effectiveText = profile.canciones_imperdibles;
      const accepted = refineSuggestions.filter((s) => refineAccepted[s.title]);
      if (accepted.length > 0) {
        // Reemplaza "Title" por "Artist - Title" en el texto solo cuando la línea
        // NO tiene ya un separador " - " o " – " (evita duplicados).
        accepted.forEach((s) => {
          const escapedTitle = s.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Match líneas que contienen SOLO el título (con posible viñeta previa)
          const re = new RegExp(
            `^(\\s*[\\u2022\\-\\*\\u00b7\\u2014>»▪▫◆◇]*\\s*)(${escapedTitle})\\s*$`,
            'gmi'
          );
          effectiveText = effectiveText.replace(re, `$1${s.suggested_artist} - $2`);
        });
      }
      const res = await axios.post(`${API_URL}/event-wizard/apply-imperdibles`, {
        text: effectiveText,
        playlist_id: eventoId,
      });
      toast.success(
        `✨ Aplicado: ${res.data.peticiones_added} URLs → Peticiones · ${res.data.songs_processed} canciones limpiadas${accepted.length ? ` · ${accepted.length} artistas resueltos por IA` : ''}`
      );
      setProfile((p) => ({ ...p, canciones_imperdibles: res.data.cleaned_text }));
      setWizardIaPreview(null);
      setRefineSuggestions([]);
      setRefineAccepted({});
      if (typeof onPlaylistSaved === 'function') {
        onPlaylistSaved();
      }
    } catch (err) {
      toast.error('No se pudo aplicar');
      console.error('[WizardIA apply] error:', err);
    } finally {
      setWizardIaApplying(false);
    }
  };

  const runRefineWithAI = async () => {
    if (!wizardIaPreview) return;
    const ambiguous = (wizardIaPreview.songs || []).filter((s) => !s.artist);
    if (ambiguous.length === 0) {
      toast.info('No hay canciones sin artista para refinar');
      return;
    }
    setRefineLoading(true);
    try {
      const res = await axios.post(`${API_URL}/event-wizard/refine-imperdibles`, {
        songs: ambiguous.map((s) => ({ title: s.title, momento: s.momento })),
        playlist_id: eventoId,
      });
      const suggestions = res.data.suggestions || [];
      setRefineSuggestions(suggestions);
      // Auto-marcar las de alta confianza como aceptadas
      const auto = {};
      suggestions.forEach((s) => { auto[s.title] = s.confidence === 'high'; });
      setRefineAccepted(auto);
      toast.success(`🤖 ${suggestions.length} sugerencias generadas (${suggestions.filter(s=>s.confidence==='high').length} alta confianza)`);
    } catch (err) {
      toast.error('No se pudo refinar');
      console.error('[WizardIA refine] error:', err);
    } finally {
      setRefineLoading(false);
    }
  };
  
  // Step 3: Generation Config
  const [config, setConfig] = useState({
    num_canciones: 60,
    usar_biblioteca: true,
    usar_tendencias: true,
    priorizar_favoritas: true,
    solo_favoritas_5_estrellas: false,
    energia_minima: null,
    // Iter88 Fase iii/iv:
    auto_calcular_num_canciones: true,    // Por defecto: deriva nº canciones de duracion_baile
    usar_playlist_spotify_cliente: true,  // Por defecto: integra playlist Spotify del cliente si existe
  });
  
  // Step 4: Generated Playlist
  const [playlistGenerada, setPlaylistGenerada] = useState(null);
  // Modo exclusivo: construir SOLO con playlists de Spotify del cliente
  const [modoExclusivoSpotify, setModoExclusivoSpotify] = useState(false);
  const [spotifyUrlsExclusivo, setSpotifyUrlsExclusivo] = useState('');
  // Feb-2026: playlists guardadas del cliente + selección múltiple.
  // clientSpotifyPlaylists: [{ label, url }]. selectedClientPlaylistUrls: Set<url>.
  // Estado unificado usado por ambos modos (exclusivo y normal).
  const [clientSpotifyPlaylists, setClientSpotifyPlaylists] = useState([]);
  const [selectedClientPlaylistUrls, setSelectedClientPlaylistUrls] = useState(new Set());
  const [showExtraUrlsInput, setShowExtraUrlsInput] = useState(false);
  // Iter92 Bloque C — Set de ids de canciones marcadas "no encaja" en esta sesión
  const [feedbackBlocked, setFeedbackBlocked] = useState(new Set());
  
  // Iter92 Bloque C — Marcar canción como "no encaja" para futuros eventos similares
  const markNoEncaja = async (song) => {
    if (!song || feedbackBlocked.has(song.id)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/dj-assistant/negative-feedback`,
        {
          song_id: song.id,
          spotify_id: song.spotify_id,
          title: song.title,
          artist: song.artist || '',
          evento_id: eventoId,
          tipo_evento: profile.tipo_evento || '',
          edad_media: profile.edad_media || '',
          region: profile.region || '',
          reason: 'marcado desde Wizard',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedbackBlocked(prev => new Set(prev).add(song.id));
      toast.success(`"${song.title}" marcada como "no encaja". No volverá en eventos similares.`);
    } catch (error) {
      console.error('Error marking no-encaja:', error);
      toast.error('Error al marcar feedback');
    }
  };

  // Control por canción (Paso 4): quitar de la playlist o dejar de marcarla como pendiente Denon.
  // Actualiza el estado local y persiste en la biblioteca (pendiente_denon) en el momento.
  const updatePlaylistGeneradaSongs = (mapperOrFilter, isFilter = false) => {
    setPlaylistGenerada(prev => {
      if (!prev) return prev;
      const canciones = isFilter ? prev.canciones.filter(mapperOrFilter) : prev.canciones.map(mapperOrFilter);
      const pendientes = canciones.filter(s => s.nueva_pendiente || s.pendiente_denon).length;
      // Recalcular resumen por género para que "Distribución por Género" quede sincronizado
      const resumen = {};
      canciones.forEach(s => {
        const g = s.genero || 'Otro';
        resumen[g] = (resumen[g] || 0) + 1;
      });
      return {
        ...prev,
        canciones,
        canciones_pendientes_denon: pendientes,
        total_canciones: canciones.length,
        resumen_por_genero: resumen,
      };
    });
  };

  // "Quitar": la saca de la playlist generada (no se guardará) y, si es placeholder nuevo, la
  // borra de la biblioteca para no dejar pendientes basura. Best-effort (delete es admin-only).
  const quitarCancionPendiente = async (song) => {
    updatePlaylistGeneradaSongs(s => (s.id !== song.id), true);
    toast.success(`"${song.title}" quitada de la playlist.`);
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/songs/${song.id}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      // Si no es admin o falla el borrado, al menos la desmarcamos como pendiente
      try {
        await axios.put(`${API_URL}/songs/${song.id}`, { pendiente_denon: false }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (_e) { /* noop */ }
    }
  };

  // "No descargar": la deja en la playlist pero desmarca pendiente_denon (la reproducirás por streaming).
  const noDescargarCancion = async (song) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API_URL}/songs/${song.id}`, { pendiente_denon: false }, { headers: { Authorization: `Bearer ${token}` } });
      updatePlaylistGeneradaSongs(s => (s.id === song.id ? { ...s, nueva_pendiente: false, pendiente_denon: false, _no_descargar: true } : s));
      toast.success(`"${song.title}" ya no se marcará para descargar (solo streaming).`);
    } catch (e) {
      console.error('Error updating pendiente_denon:', e);
      toast.error('No se pudo actualizar la canción.');
    }
  };
  
  // Load initial profile
  useEffect(() => {
    loadProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: loadProfile
  }, [eventoId]);
  
  // Parser de `porcentajes_estilos` guardados en los detalles del evento.
  // Soporta ambos formatos:
  //   - "30% Pop español, 20% Reggaeton, 10% 80s"
  //   - "Pop español:30, Reggaeton:20, 80s:10"
  // Devuelve array con shape compatible con `generosSugeridos`.
  const parseEventPercentages = (str) => {
    if (!str || typeof str !== 'string') return [];
    const result = [];
    const parts = str.split(',').map(p => p.trim()).filter(Boolean);
    for (const part of parts) {
      // Formato "30% Pop español" o "Pop español 30%"
      let m = part.match(/^(\d+)\s*%\s*(.+)$/);
      if (m) {
        const pct = parseInt(m[1], 10);
        const genre = m[2].trim();
        if (genre && !Number.isNaN(pct) && pct > 0) {
          result.push({ genero: genre, porcentaje: pct });
        }
        continue;
      }
      m = part.match(/^(.+?)\s+(\d+)\s*%$/);
      if (m) {
        const pct = parseInt(m[2], 10);
        const genre = m[1].trim();
        if (genre && !Number.isNaN(pct) && pct > 0) {
          result.push({ genero: genre, porcentaje: pct });
        }
        continue;
      }
      // Formato "Género:30"
      m = part.match(/^(.+?):\s*(\d+)$/);
      if (m) {
        const pct = parseInt(m[2], 10);
        const genre = m[1].trim();
        if (genre && !Number.isNaN(pct) && pct > 0) {
          result.push({ genero: genre, porcentaje: pct });
        }
      }
    }
    return result;
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/event-wizard/profile/${eventoId}`);
      setProfile(prev => ({
        ...prev,
        ...response.data,
        evento_id: eventoId
      }));

      // Prefill URLs de Spotify del cliente (para el modo exclusivo)
      // Feb-2026: además construimos la lista `clientSpotifyPlaylists` con
      // etiqueta legible + URL para el nuevo selector de checkboxes.
      const urlsCliente = [];
      const playlistsCliente = [];
      const seenClientUrls = new Set();
      const pushClientPlaylist = (label, url) => {
        const u = (url || '').trim();
        if (!u || seenClientUrls.has(u)) return;
        seenClientUrls.add(u);
        urlsCliente.push(u);
        playlistsCliente.push({ label: (label || '').trim() || 'Playlist Spotify', url: u });
      };
      if (response.data.playlist_spotify) {
        pushClientPlaylist('Playlist principal', response.data.playlist_spotify);
      }
      (response.data.playlist_spotify_urls || []).forEach((it, idx) => {
        if (it?.url) pushClientPlaylist(it.label || `Playlist ${idx + 2}`, it.url);
      });
      // Feb-2026 BUGFIX: NO pre-rellenar el textarea con las URLs del cliente.
      // Antes se hacía y provocaba que, aunque el DJ desmarcara playlists en los
      // checkboxes, esas mismas URLs seguían viviendo en `spotifyUrlsExclusivo` y
      // se enviaban igualmente al backend (unión checkboxes ∪ textarea). Resultado:
      // la selección de checkboxes no se respetaba y se usaban SIEMPRE todas las
      // playlists del cliente. El textarea queda EXCLUSIVAMENTE para URLs extra.
      setClientSpotifyPlaylists(playlistsCliente);
      // Por defecto todas seleccionadas
      setSelectedClientPlaylistUrls(new Set(playlistsCliente.map((p) => p.url)));
      
      // Cargar estado guardado del wizard si existe
      const wizardState = response.data.wizard_state;
      if (wizardState && wizardState.generos_sugeridos?.length > 0) {
        setGenerosSugeridos(wizardState.generos_sugeridos);
        if (wizardState.config) {
          setConfig(prev => ({...prev, ...wizardState.config}));
        }
        // Mostrar mensaje de que se recuperó el estado
        toast.info(`Recuperado estado anterior (paso ${wizardState.last_step})`);
      } else if (response.data.porcentajes_estilos) {
        // Fix feb/2026: si el usuario ya configuró porcentajes en los detalles
        // del evento, los usamos como punto de partida en el wizard en vez de
        // ignorarlos y pedir sugerencia IA desde cero. El usuario aún puede
        // pulsar "Pedir sugerencia IA" manualmente para regenerar si quiere.
        const parsed = parseEventPercentages(response.data.porcentajes_estilos);
        if (parsed.length > 0) {
          setGenerosSugeridos(parsed);
          setHasManualPercentages(true);
          setExplicacionIA(
            '📊 Cargados desde los porcentajes que ya definiste en los detalles del evento. ' +
            'Puedes pulsar "Siguiente" para usarlos tal cual o "Pedir sugerencia IA" para que la IA los complemente.'
          );
          toast.success(`📊 Cargados ${parsed.length} estilos desde los detalles del evento`);
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Error al cargar datos del evento');
    } finally {
      setLoading(false);
    }
  };
  
  // Guardar estado del wizard
  const saveWizardState = async (step) => {
    try {
      await axios.post(`${API_URL}/event-wizard/save-state`, {
        evento_id: eventoId,
        generos_sugeridos: generosSugeridos,
        config: config,
        current_step: step
      });
    } catch (error) {
      console.error('Error saving wizard state:', error);
      // No mostrar error al usuario, es solo para persistencia
    }
  };
  
  // Step 2: Get AI suggestions
  // Iter fix (feb-2026): usa el patrón async job + polling para evitar timeouts
  // del edge/proxy (iOS Safari cerraba fetch de 25-30s antes de recibir respuesta).
  const getSuggestions = async () => {
    setAnalyzingWithAI(true);
    try {
      const payload = {
        evento_id: eventoId,
        gustos_musicales: profile.gustos_musicales,
        tipo_evento: profile.tipo_evento,
        edad_media: profile.edad_media,
        region: profile.region,
        canciones_prohibidas: profile.canciones_prohibidas,
        porcentajes_estilos: profile.porcentajes_estilos || '',
        respetar_base: respetarBase
      };
      // 1. Start job
      const startRes = await axios.post(
        `${API_URL}/event-wizard/suggest-genres-async/start`,
        payload,
        { timeout: 15000 }
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job_id returned');
      // 2. Poll cada 2.5s hasta done/error (máx 90s = 36 intentos)
      let attempts = 0;
      const maxAttempts = 36;
      let finalResult = null;
      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 2500));
        attempts += 1;
        try {
          const pollRes = await axios.get(
            `${API_URL}/event-wizard/suggest-genres-async/status/${jobId}`,
            // Feb-2026: timeout 60s (antes 10s). El event loop se bloquea
            // temporalmente durante el LLM síncrono, provocando falsos
            // ReadTimeouts aunque el job siga corriendo correctamente.
            { timeout: 60000 }
          );
          const status = pollRes.data?.status;
          if (status === 'done') {
            finalResult = pollRes.data.result;
            break;
          }
          if (status === 'error') {
            throw new Error(pollRes.data?.error || 'Error en el análisis IA');
          }
          // status === 'running' → seguir polling
        } catch (pollErr) {
          // Errores de red transitorios: seguir polling salvo 4xx no-recuperables
          const s = pollErr.response?.status;
          if (s === 401 || s === 403 || s === 404) throw pollErr;
          console.warn('[Wizard] poll transient error, retrying...', pollErr.message);
        }
      }
      if (!finalResult) {
        throw new Error('La sugerencia IA tardó demasiado (>90s). Intenta de nuevo.');
      }
      setGenerosSugeridos(finalResult.sugerencias || []);
      setExplicacionIA(finalResult.explicacion || '');
      setGenerosDisponibles(finalResult.generos_disponibles || []);
      setCurrentStep(2);
      setTimeout(() => saveWizardState(2), 500);
    } catch (error) {
      console.error('Error getting suggestions:', error);
      const detail = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al obtener sugerencias de la IA: ${detail}`);
    } finally {
      setAnalyzingWithAI(false);
    }
  };
  
  // Saltar la IA y usar directamente los porcentajes manuales del evento
  const skipAIUseManual = async () => {
    if (generosSugeridos.length === 0) {
      toast.error('No hay porcentajes manuales cargados');
      return;
    }
    setAnalyzingWithAI(true);
    // Cargar géneros disponibles de la biblioteca sin pedir sugerencia a la IA
    try {
      const response = await axios.post(`${API_URL}/event-wizard/suggest-genres`, {
        evento_id: eventoId,
        gustos_musicales: profile.gustos_musicales,
        tipo_evento: profile.tipo_evento,
        edad_media: profile.edad_media,
        region: profile.region,
        canciones_prohibidas: profile.canciones_prohibidas,
        porcentajes_estilos: profile.porcentajes_estilos || '',
        respetar_base: true  // Modo estricto: no se llama a la IA, se devuelven los porcentajes tal cual
      });
      
      // Solo actualizar los géneros disponibles y la explicación
      setGenerosDisponibles(response.data.generos_disponibles || []);
      if (response.data.sugerencias?.length > 0) {
        setGenerosSugeridos(response.data.sugerencias);
      }
      setExplicacionIA(response.data.explicacion || '📊 Usando tus porcentajes manuales tal cual');
      setCurrentStep(2);
      setTimeout(() => saveWizardState(2), 500);
    } catch (error) {
      console.error('Error loading manual percentages:', error);
      toast.error('Error al cargar porcentajes');
    } finally {
      setAnalyzingWithAI(false);
    }
  };
  
  // Construir playlist SOLO con playlists de Spotify del cliente (modo exclusivo)
  // Feb-2026: usa checkboxes seleccionados + URLs extra opcionales del textarea.
  const buildFromClientSpotify = async () => {
    const extraUrls = (spotifyUrlsExclusivo || '')
      .split('\n')
      .map((u) => u.trim())
      .filter(Boolean);
    const selected = Array.from(selectedClientPlaylistUrls);
    const urls = Array.from(new Set([...selected, ...extraUrls]));
    if (urls.length === 0) {
      toast.error('Selecciona al menos una playlist o pega una URL.');
      return;
    }
    setLoading(true);
    try {
      const spotifyAccessToken = localStorage.getItem('spotify_access_token') || null;
      const response = await axios.post(`${API_URL}/event-wizard/build-from-client-spotify`, {
        evento_id: eventoId,
        urls,
        spotify_access_token: spotifyAccessToken
      }, { timeout: 240000 });
      setPlaylistGenerada(response.data);
      setCurrentStep(4);
      saveWizardState(4);
      const {
        total_canciones,
        canciones_nuevas_biblioteca,
        canciones_enriquecidas,
        imperdibles_added,
        hora_loca_added,
        tracks_filtrados_por_prohibidas,
      } = response.data || {};
      if (total_canciones > 0) {
        const extra = canciones_enriquecidas > 0
          ? ` · ${canciones_enriquecidas} clasificadas con IA en tus estilos`
          : '';
        toast.success(
          `${total_canciones} canciones · ${canciones_nuevas_biblioteca} nuevas pendientes de descargar${extra}`
        );
      }
      if (imperdibles_added > 0 || hora_loca_added) {
        const parts = [];
        if (imperdibles_added > 0) parts.push(`${imperdibles_added} imperdible(s)`);
        if (hora_loca_added) parts.push('canción hora loca');
        toast.info(`Añadidas al final: ${parts.join(' + ')}`);
      }
      if (tracks_filtrados_por_prohibidas > 0) {
        toast.warning(`${tracks_filtrados_por_prohibidas} canción(es) excluida(s) por prohibidas.`);
      }
      if (response.data.warnings?.length > 0) response.data.warnings.forEach(w => toast.warning(w));
      if (total_canciones === 0) toast.error('No se encontraron canciones en las playlists indicadas.');
    } catch (error) {
      console.error('Error building from client spotify:', error);
      toast.error(error.response?.data?.detail || 'Error al construir la playlist desde Spotify.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Generate playlist
  const generatePlaylist = async () => {
    // Modo exclusivo: solo playlists de Spotify del cliente
    if (modoExclusivoSpotify) {
      await buildFromClientSpotify();
      return;
    }

    // Validar que hay géneros seleccionados
    if (!generosSugeridos || generosSugeridos.length === 0) {
      toast.error('No hay géneros seleccionados. Añade al menos un género.');
      return;
    }
    
    const totalPorcentaje = generosSugeridos.reduce((sum, g) => sum + (g.porcentaje || 0), 0);
    if (totalPorcentaje === 0) {
      toast.error('Los porcentajes no pueden ser todos 0.');
      return;
    }
    
    setLoading(true);
    try {
      const generos_porcentajes = {};
      generosSugeridos.forEach(g => {
        if (g.porcentaje > 0) {
          generos_porcentajes[g.genero] = g.porcentaje;
        }
      });
      
      // Iter88: token Spotify del DJ es requerido para leer playlists (cambio API nov-2024)
      const spotifyAccessToken = localStorage.getItem('spotify_access_token') || null;

      // Feb-2026: si el usuario deseleccionó playlists, mandamos las URLs elegidas
      // en `client_spotify_urls_override`. El backend usará solo esas.
      const overrideUrls = (
        config.usar_playlist_spotify_cliente
        && clientSpotifyPlaylists.length > 1
        && selectedClientPlaylistUrls.size < clientSpotifyPlaylists.length
      )
        ? Array.from(selectedClientPlaylistUrls)
        : null;

      const payload = {
        evento_id: eventoId,
        generos_porcentajes,
        ...config,
        spotify_access_token: spotifyAccessToken,
        ...(overrideUrls ? { client_spotify_urls_override: overrideUrls } : {}),
      };

      // La generación con pipeline Spotify + afines LLM puede tardar >30s y provocar
      // 504 en el ingress. Usamos job async + polling (igual que extraer-canciones).
      const token = localStorage.getItem('token');
      const sleep = (ms) => new Promise(r => setTimeout(r, ms));

      const startRes = await axios.post(`${API_URL}/event-wizard/generate-playlist-async/start`, payload);
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No se recibió job_id del servidor');

      let data = null;
      const maxAttempts = 120; // 120 × 3s = 6 min
      for (let i = 0; i < maxAttempts; i++) {
        await sleep(3000);
        let status;
        try {
          const pollRes = await axios.get(
            `${API_URL}/event-wizard/generate-playlist-async/status/${jobId}`,
            {
              // Feb-2026: timeout explícito 60s. El event loop se bloquea
              // temporalmente durante llamadas LLM síncronas → sin timeout
              // el polling veía ReadTimeouts aunque el job siguiera corriendo.
              timeout: 60000,
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }
          );
          status = pollRes.data;
        } catch (pollErr) {
          continue; // error de red transitorio: seguir intentando
        }
        if (!status || status.status === 'running') continue;
        if (status.status === 'error') {
          throw new Error(status.error || 'Error generando la playlist');
        }
        if (status.status === 'done') {
          data = status.result || {};
          break;
        }
      }

      if (!data) {
        toast.error('La generación tardó demasiado (>6 min). Inténtalo de nuevo.');
        return;
      }

      setPlaylistGenerada(data);
      setCurrentStep(4);
      
      // Guardar estado con la config final
      saveWizardState(4);
      
      if (data.warnings?.length > 0) {
        data.warnings.forEach(w => toast.warning(w));
      }
      
      if (data.total_canciones === 0) {
        toast.error('No se encontraron canciones con los géneros seleccionados. Prueba con otros géneros.');
      }
    } catch (error) {
      console.error('Error generating playlist:', error);
      const detail = error?.response?.data?.detail || error?.message;
      toast.error(detail ? `Error al generar playlist: ${detail}` : 'Error al generar playlist. Revisa los géneros seleccionados.');
    } finally {
      setLoading(false);
    }
  };
  
  const [saveMode, setSaveMode] = useState('append'); // Iter89: 'append' (NO sobrescribe) | 'replace'
  
  // Step 4: Save playlist
  const savePlaylist = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/event-wizard/save-playlist/${eventoId}`,
        playlistGenerada.canciones,
        { params: { mode: saveMode } }
      );
      
      const { added, skipped_already_present, total_canciones, previous_count, mode } = response.data || {};
      const partes = [];
      if (mode === 'append') {
        partes.push(`Playlist actualizada: ${added} ${added === 1 ? 'canción nueva añadida' : 'canciones nuevas añadidas'}`);
        if (skipped_already_present > 0) partes.push(`${skipped_already_present} ya estaban`);
        partes.push(`(antes: ${previous_count}, ahora: ${total_canciones})`);
      } else {
        partes.push(`Playlist reemplazada con ${total_canciones} canciones`);
        if (previous_count > 0) partes.push(`(antes había ${previous_count})`);
      }
      toast.success(partes.join(' · '));
      
      // Wait for the parent to refresh data before closing
      if (onPlaylistSaved) {
        await onPlaylistSaved();
      }
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving playlist:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar playlist');
    } finally {
      setSaving(false);
    }
  };
  
  // Adjust genre percentage (Iter89.6: SIN redistribución automática — el DJ ajusta libremente)
  // El total puede no ser 100% mientras el DJ trabaja; se muestra un indicador y un botón "Normalizar".
  // El backend normaliza internamente (porcentaje / total) para calcular num_canciones por género.
  const adjustGenrePercentage = (index, delta) => {
    const newSugerencias = [...generosSugeridos];
    const newValue = Math.max(0, Math.min(100, newSugerencias[index].porcentaje + delta));
    if (newValue === newSugerencias[index].porcentaje) return;
    newSugerencias[index].porcentaje = newValue;
    setGenerosSugeridos(newSugerencias);
  };
  
  // Iter89.6: Normalizar todos los porcentajes proporcionalmente para que sumen 100%
  const normalizePercentages = () => {
    const total = generosSugeridos.reduce((sum, g) => sum + g.porcentaje, 0);
    if (total === 0) {
      // Si todo está a 0, repartir equitativamente
      const perGenre = Math.floor(100 / generosSugeridos.length);
      const remainder = 100 - perGenre * generosSugeridos.length;
      const newSugerencias = generosSugeridos.map((g, i) => ({
        ...g, porcentaje: perGenre + (i === 0 ? remainder : 0)
      }));
      setGenerosSugeridos(newSugerencias);
      toast.success(`Normalizado a 100% (${perGenre}% por género)`);
      return;
    }
    if (total === 100) {
      toast.info('Ya suma 100% exactamente');
      return;
    }
    // Escalar proporcionalmente
    const factor = 100 / total;
    const newSugerencias = generosSugeridos.map(g => ({
      ...g, porcentaje: Math.round(g.porcentaje * factor)
    }));
    // Ajustar diferencia de redondeo en el género de mayor peso
    const newTotal = newSugerencias.reduce((s, g) => s + g.porcentaje, 0);
    if (newTotal !== 100 && newSugerencias.length > 0) {
      const largestIdx = newSugerencias.reduce((maxIdx, g, i, arr) =>
        g.porcentaje > arr[maxIdx].porcentaje ? i : maxIdx, 0);
      newSugerencias[largestIdx].porcentaje += (100 - newTotal);
    }
    setGenerosSugeridos(newSugerencias);
    toast.success(`Normalizado de ${total}% a 100%`);
  };
  
  // Iter89.7: Presets rápidos de porcentajes — distribuciones típicas DJ profesional
  // Cada preset reemplaza completamente la configuración. Suman 100% exactos.
  // Iter89.8: editables + persistencia localStorage + custom presets del DJ.
  const DEFAULT_GENRE_PRESETS = [
    {
      key: 'boda-joven',
      label: '🎉 Boda joven (25-40)',
      desc: 'Reggaeton + Pop + Dance · alta energía',
      distribution: {
        'Reggaeton': 25, 'Pop español': 20, 'Pop internacional': 20,
        'Dance': 15, 'Bachata': 10, 'Indie español': 10
      }
    },
    {
      key: 'boda-clasica',
      label: '💍 Boda clásica (40+)',
      desc: 'Clásicos atemporales · 70s/80s/90s',
      distribution: {
        'Pop clásico español': 25, 'Pop clásico internacional': 20,
        'Rock clásico español': 15, 'Rock clásico internacional': 15,
        'Disco': 15, 'Bachata': 10
      }
    },
    {
      key: 'boda-mixta',
      label: '👰 Boda mixta (todas edades)',
      desc: 'Equilibrio entre clásico y actual',
      distribution: {
        'Pop': 20, 'Pop clásico internacional': 15, 'Pop clásico español': 15,
        'Reggaeton': 15, 'Disco': 10, 'Dance': 10, 'Bachata': 10, 'Rock clásico internacional': 5
      }
    },
    {
      key: 'corporativo',
      label: '🏢 Corporativo elegante',
      desc: 'Pop internacional + clásicos · vibra refinada',
      distribution: {
        'Pop internacional': 30, 'Pop español': 20, 'Disco': 15,
        'Pop clásico internacional': 15, 'Dance': 10, '80s': 10
      }
    },
    {
      key: 'coctel',
      label: '🍸 Cóctel / aperitivo',
      desc: 'Lounge · jazzy · suave',
      distribution: {
        'Pop clásico internacional': 25, 'Pop clásico español': 20,
        'Bossa-nova': 15, 'Jazz': 15, 'Pop internacional': 15, '80s': 10
      }
    },
    {
      key: 'bodas-oro',
      label: '🎊 Bodas de oro / 50+',
      desc: 'Clásicos españoles + internacionales',
      distribution: {
        'Pop clásico español': 30, 'Pop clásico internacional': 25,
        'Rock clásico español': 15, 'Rock clásico internacional': 15, 'Disco': 15
      }
    },
    {
      key: 'fiesta-maxima',
      label: '🔥 Fiesta máxima energía',
      desc: 'Pista llena · pico continuo',
      distribution: {
        'Reggaeton': 30, 'Dance': 25, 'Pop': 20, 'EDM': 15, 'Hip Hop': 10
      }
    }
  ];
  
  const PRESETS_STORAGE_KEY = 'wizard_genre_presets_v1';
  
  const [genrePresets, setGenrePresets] = useState(() => {
    try {
      const raw = localStorage.getItem(PRESETS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.warn('[Wizard] Error leyendo presets de localStorage:', e);
    }
    return DEFAULT_GENRE_PRESETS;
  });
  
  // Persistir cambios en localStorage automáticamente
  useEffect(() => {
    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(genrePresets));
    } catch (e) {
      console.warn('[Wizard] Error guardando presets en localStorage:', e);
    }
  }, [genrePresets]);
  
  // Edit dialog state
  const [editingPreset, setEditingPreset] = useState(null); // {key, label, desc, distribution} en edición
  const [savePresetDialog, setSavePresetDialog] = useState(false);
  const [newPresetForm, setNewPresetForm] = useState({ label: '', desc: '' });
  
  const applyGenrePreset = (preset) => {
    const newSugerencias = Object.entries(preset.distribution).map(([genero, porcentaje]) => ({
      genero,
      porcentaje,
      motivo: `Preset: ${preset.label}`
    }));
    setGenerosSugeridos(newSugerencias);
    toast.success(`Aplicado preset: ${preset.label}`);
  };
  
  const deletePreset = (key) => {
    if (genrePresets.length <= 1) {
      toast.error('Debe quedar al menos 1 preset');
      return;
    }
    setGenrePresets(prev => prev.filter(p => p.key !== key));
    toast.success('Preset eliminado');
  };
  
  const updatePreset = () => {
    if (!editingPreset) return;
    if (!editingPreset.label.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    setGenrePresets(prev => prev.map(p => p.key === editingPreset.key ? { ...editingPreset } : p));
    setEditingPreset(null);
    toast.success('Preset actualizado');
  };
  
  const restoreDefaultPresets = () => {
    if (!window.confirm('¿Restaurar los 7 presets por defecto? Tus presets personalizados se mantendrán; los defaults eliminados volverán a aparecer.')) {
      return;
    }
    setGenrePresets(prev => {
      const existingKeys = new Set(prev.map(p => p.key));
      const restored = [...prev];
      DEFAULT_GENRE_PRESETS.forEach(d => {
        if (!existingKeys.has(d.key)) {
          restored.push({ ...d });
        }
      });
      return restored;
    });
    toast.success('Presets por defecto restaurados');
  };
  
  const saveCurrentAsPreset = () => {
    if (generosSugeridos.length === 0) {
      toast.error('Añade al menos un género primero');
      return;
    }
    const total = generosSugeridos.reduce((sum, g) => sum + g.porcentaje, 0);
    if (total === 0) {
      toast.error('Los porcentajes no pueden ser todos 0');
      return;
    }
    setNewPresetForm({ label: '', desc: '' });
    setSavePresetDialog(true);
  };
  
  const confirmSaveCurrentAsPreset = () => {
    const label = newPresetForm.label.trim();
    if (!label) {
      toast.error('Pon un nombre al preset');
      return;
    }
    // Construir distribution desde la config actual (normalizando a 100%)
    const total = generosSugeridos.reduce((s, g) => s + g.porcentaje, 0);
    const factor = total > 0 ? 100 / total : 1;
    const distribution = {};
    generosSugeridos.forEach(g => {
      if (g.porcentaje > 0) {
        distribution[g.genero] = Math.round(g.porcentaje * factor);
      }
    });
    // Ajuste fino para sumar 100% exacto
    const sumOk = Object.values(distribution).reduce((s, v) => s + v, 0);
    if (sumOk !== 100 && Object.keys(distribution).length > 0) {
      const firstKey = Object.keys(distribution)[0];
      distribution[firstKey] += (100 - sumOk);
    }
    
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `custom-${Date.now()}`;
    const newKey = `custom-${slug}-${Date.now().toString(36)}`;
    const newPreset = {
      key: newKey,
      label,
      desc: newPresetForm.desc.trim() || 'Preset personalizado',
      distribution,
      _custom: true,
    };
    setGenrePresets(prev => [...prev, newPreset]);
    setSavePresetDialog(false);
    toast.success(`Preset "${label}" guardado`);
  };
  
  // Add new genre
  const addGenre = (genero) => {
    if (generosSugeridos.some(g => g.genero === genero)) return;
    
    // Take 10% from largest genre
    const newSugerencias = [...generosSugeridos];
    if (newSugerencias.length > 0) {
      const largestIdx = newSugerencias.reduce((maxIdx, g, i, arr) => 
        g.porcentaje > arr[maxIdx].porcentaje ? i : maxIdx, 0);
      newSugerencias[largestIdx].porcentaje -= 10;
    }
    
    newSugerencias.push({
      genero,
      porcentaje: 10,
      motivo: 'Añadido manualmente'
    });
    
    setGenerosSugeridos(newSugerencias);
  };
  
  // Remove genre (Iter89.6: SIN redistribución; el DJ puede normalizar manualmente)
  const removeGenre = (index) => {
    if (generosSugeridos.length <= 2) {
      toast.error('Necesitas al menos 2 géneros');
      return;
    }
    const newSugerencias = generosSugeridos.filter((_, i) => i !== index);
    setGenerosSugeridos(newSugerencias);
  };
  
  // Step indicators
  const steps = [
    { num: 1, title: 'Perfil', icon: Users },
    { num: 2, title: 'Géneros', icon: Music },
    { num: 3, title: 'Opciones', icon: Settings },
    { num: 4, title: 'Revisar', icon: Check }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl bg-slate-900 border-slate-700 max-h-[90vh] flex flex-col">
        <CardHeader className="border-b border-slate-700 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-cyan-400" />
                Preparar Evento
              </CardTitle>
              <CardDescription className="text-slate-400">
                {profile.nombre_evento} {profile.fecha && `• ${profile.fecha}`}
              </CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                if (analyzingWithAI) {
                  const ok = window.confirm(
                    '⏳ La IA aún está analizando (~20-30s). ¿Seguro que quieres cerrar? Perderás la sugerencia.'
                  );
                  if (!ok) return;
                }
                onClose();
              }}
              className="text-slate-400 hover:text-white"
              data-testid="wizard-close-btn"
            >
              ✕
            </Button>
          </div>
          
          {/* Step indicators */}
          <div className="flex items-center justify-between mt-4 px-4">
            {steps.map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <div 
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
                    currentStep === step.num 
                      ? 'bg-cyan-500/20 text-cyan-400' 
                      : currentStep > step.num 
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-slate-800 text-slate-500'
                  }`}
                >
                  <step.icon className="w-4 h-4" />
                  <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                </div>
                {idx < steps.length - 1 && (
                  <ChevronRight className={`w-4 h-4 mx-2 ${
                    currentStep > step.num ? 'text-green-400' : 'text-slate-600'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-auto p-6">
          {analyzingWithAI ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="wizard-analyzing-with-ai">
              <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
              <div className="text-center">
                <p className="text-lg font-semibold text-cyan-300">🤖 La IA está analizando tu evento…</p>
                <p className="text-sm text-slate-400 mt-2">
                  Esto suele tardar entre <strong>20 y 30 segundos</strong>. No cierres la ventana.
                </p>
              </div>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              <span className="ml-3 text-slate-400">
                {currentStep === 1 ? 'Cargando datos del evento…' : 
                 currentStep === 3 ? 'Generando playlist…' : 
                 'Cargando…'}
              </span>
            </div>
          ) : (
            <>
              {/* STEP 1: Event Profile */}
              {currentStep === 1 && (
                <div className="space-y-6" data-testid="wizard-step1-profile">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Tipo de Evento</Label>
                      <Input
                        value={profile.tipo_evento}
                        onChange={(e) => setProfile({...profile, tipo_evento: e.target.value})}
                        placeholder="Boda, Cumpleaños, Corporativo..."
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Edad Media Invitados</Label>
                      <Input
                        value={profile.edad_media}
                        onChange={(e) => setProfile({...profile, edad_media: e.target.value})}
                        placeholder="30-45 años"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Gustos Musicales del Cliente</Label>
                    <Textarea
                      value={profile.gustos_musicales}
                      onChange={(e) => setProfile({...profile, gustos_musicales: e.target.value})}
                      placeholder="Pop español, reggaeton suave, clásicos de los 80, nada de heavy..."
                      className="mt-1 bg-slate-800 border-slate-600 text-white min-h-[80px]"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Región</Label>
                      <Input
                        value={profile.region}
                        onChange={(e) => setProfile({...profile, region: e.target.value})}
                        placeholder="España, Andalucía..."
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 text-red-400" />
                        Canciones Prohibidas
                      </Label>
                      <Input
                        value={profile.canciones_prohibidas}
                        onChange={(e) => setProfile({...profile, canciones_prohibidas: e.target.value})}
                        placeholder="Separadas por comas..."
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-400" />
                      Canciones Imprescindibles
                    </Label>
                    <textarea
                      value={profile.canciones_imperdibles}
                      onChange={(e) => setProfile({...profile, canciones_imperdibles: e.target.value})}
                      placeholder="Pega el texto del cliente tal cual (viñetas, URLs YouTube, secciones BAILE GENERAL/HORA LOCA/CIERRE...). El botón 'Optimizar con IA' lo limpia y extrae los enlaces."
                      rows={5}
                      className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 text-white text-sm px-3 py-2 font-mono resize-y"
                      data-testid="wizard-imperdibles-textarea"
                    />
                    <div className="mt-2 flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={!profile.canciones_imperdibles || !profile.canciones_imperdibles.trim() || wizardIaLoading}
                        onClick={runWizardIaPreview}
                        className="bg-purple-700 hover:bg-purple-600 text-white"
                        data-testid="wizard-ia-preview-btn"
                      >
                        {wizardIaLoading ? 'Analizando…' : '✨ Optimizar con IA'}
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 flex items-center gap-1">
                      <Zap className="w-3 h-3 text-orange-400" />
                      Canción Inicio Hora Loca
                    </Label>
                    <Input
                      value={profile.hora_loca_cancion || ''}
                      onChange={(e) => setProfile({...profile, hora_loca_cancion: e.target.value})}
                      placeholder="Canción para arrancar la hora loca..."
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                  
                  {/* Resumen de datos del cliente */}
                  {(profile.gustos_musicales || profile.canciones_imperdibles || profile.canciones_prohibidas || profile.hora_loca_cancion) && (
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                      <p className="text-xs text-slate-400 mb-2">📋 Datos del cliente cargados:</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {profile.gustos_musicales && (
                          <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded">
                            Gustos: {profile.gustos_musicales.substring(0, 30)}...
                          </span>
                        )}
                        {profile.canciones_imperdibles && (
                          <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded">
                            Imprescindibles: ✓
                          </span>
                        )}
                        {profile.canciones_prohibidas && (
                          <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded">
                            Prohibidas: ✓
                          </span>
                        )}
                        {profile.hora_loca_cancion && (
                          <span className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded">
                            Hora Loca: ✓
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Porcentajes manuales detectados */}
                  {hasManualPercentages && generosSugeridos.length > 0 && (
                    <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/40 rounded-lg p-4" data-testid="manual-percentages-notice">
                      <div className="flex items-start gap-2 mb-3">
                        <Sparkles className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-cyan-200 mb-1">
                            📊 Porcentajes manuales detectados en los detalles del evento
                          </p>
                          <p className="text-xs text-slate-400">
                            {generosSugeridos.map(g => `${g.porcentaje}% ${g.genero}`).join(' • ')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 p-2 bg-slate-800/60 rounded border border-slate-700">
                        <Checkbox
                          id="respetar-base"
                          checked={respetarBase}
                          onCheckedChange={setRespetarBase}
                          className="mt-0.5"
                          data-testid="respetar-base-checkbox"
                        />
                        <Label htmlFor="respetar-base" className="text-xs text-slate-300 cursor-pointer leading-relaxed">
                          <span className="font-medium text-white">Respetar mis porcentajes como base obligatoria.</span>
                          <br />
                          <span className="text-slate-500">
                            {respetarBase
                              ? 'Al pedir sugerencia IA, tus porcentajes se mantendrán tal cual (la IA no los modificará).'
                              : 'Al pedir sugerencia IA, la IA podrá reinterpretar tus porcentajes libremente.'}
                          </span>
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* STEP 2: Genre Mix */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  {explicacionIA && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-5 h-5 text-cyan-400 mt-0.5" />
                        <p className="text-sm text-cyan-200">{explicacionIA}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Iter89.7+8: Presets rápidos de porcentajes — editables y guardables */}
                  <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <Label className="text-xs uppercase tracking-wider text-slate-400">
                        ⚡ Presets rápidos
                      </Label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={saveCurrentAsPreset}
                          className="text-[11px] px-2 py-1 rounded bg-cyan-900/30 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-800/50 flex items-center gap-1"
                          title="Guarda la configuración actual de géneros como nuevo preset personalizado"
                          data-testid="wizard-save-current-as-preset-btn"
                        >
                          <Save className="w-3 h-3" /> Guardar actual
                        </button>
                        <button
                          type="button"
                          onClick={restoreDefaultPresets}
                          className="text-[11px] px-2 py-1 rounded bg-slate-900/50 border border-slate-600 text-slate-400 hover:text-slate-200 hover:border-slate-500 flex items-center gap-1"
                          title="Restaurar los 7 presets por defecto"
                          data-testid="wizard-restore-default-presets-btn"
                        >
                          <RotateCcw className="w-3 h-3" /> Restaurar
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {genrePresets.map(preset => (
                        <div
                          key={preset.key}
                          className="relative group bg-slate-900/60 border border-slate-700 hover:border-cyan-500/60 hover:bg-cyan-900/20 rounded-lg transition-colors"
                          data-testid={`wizard-preset-${preset.key}`}
                        >
                          <button
                            type="button"
                            onClick={() => applyGenrePreset(preset)}
                            className="text-left px-3 py-2 pr-12 w-full"
                            title={`${preset.desc}\n\nClick para aplicar`}
                          >
                            <div className="text-sm text-white font-medium group-hover:text-cyan-300">
                              {preset.label}
                            </div>
                            <div className="text-[10px] text-slate-500 group-hover:text-slate-400">
                              {preset.desc}
                            </div>
                          </button>
                          {/* Action icons (edit + delete) */}
                          <div className="absolute right-1 top-1 flex flex-col gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setEditingPreset({ ...preset }); }}
                              className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-cyan-300"
                              title="Editar preset"
                              data-testid={`wizard-preset-edit-${preset.key}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deletePreset(preset.key); }}
                              className="p-1 rounded hover:bg-rose-900/40 text-slate-400 hover:text-rose-300"
                              title="Eliminar preset"
                              data-testid={`wizard-preset-delete-${preset.key}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {generosSugeridos.map((genero, idx) => (
                      <div 
                        key={genero.genero}
                        className="bg-slate-800 rounded-lg p-4 border border-slate-700"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{genero.genero}</span>
                            <Badge variant="outline" className="text-cyan-400 border-cyan-400/50">
                              {genero.porcentaje}%
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-white"
                              onClick={(e) => adjustGenrePercentage(idx, e.shiftKey ? -5 : -1)}
                              title="Click: -1% · Shift+Click: -5%"
                              data-testid={`genre-decrease-btn-${idx}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-white"
                              onClick={(e) => adjustGenrePercentage(idx, e.shiftKey ? 5 : 1)}
                              title="Click: +1% · Shift+Click: +5%"
                              data-testid={`genre-increase-btn-${idx}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-400 hover:text-red-300"
                              onClick={() => removeGenre(idx)}
                            >
                              ✕
                            </Button>
                          </div>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-cyan-500 to-cyan-400 h-2 rounded-full transition-all"
                            style={{ width: `${genero.porcentaje}%` }}
                          />
                        </div>
                        {genero.motivo && (
                          <p className="text-xs text-slate-500 mt-2">{genero.motivo}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Add genre */}
                  {generosDisponibles.length > 0 && (
                    <div>
                      <Label className="text-slate-400 text-sm">Añadir género:</Label>
                      <ScrollArea className="h-32 mt-2">
                        <div className="flex flex-wrap gap-2 pr-3">
                          {generosDisponibles
                            .filter(g => {
                              // Normalizar para comparación (sin tildes, minúsculas)
                              const normalizeStr = (str) => str.toLowerCase()
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .trim();
                              const gNorm = normalizeStr(g);
                              // Excluir géneros ya seleccionados
                              return !generosSugeridos.some(s => normalizeStr(s.genero) === gNorm);
                            })
                            .map(genero => (
                              <Badge
                                key={genero}
                                variant="outline"
                                className="cursor-pointer hover:bg-slate-700 text-slate-400 border-slate-600"
                                onClick={() => addGenre(genero)}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                {genero}
                              </Badge>
                            ))
                          }
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-slate-800/50 border-slate-700">
                    <div className="flex items-baseline gap-2">
                      <span className="text-slate-400 text-sm">Total:</span>
                      {(() => {
                        const total = generosSugeridos.reduce((sum, g) => sum + g.porcentaje, 0);
                        const isExact = total === 100;
                        const isClose = total >= 95 && total <= 105;
                        const colorClass = isExact ? 'text-emerald-400' : isClose ? 'text-amber-400' : 'text-rose-400';
                        return (
                          <>
                            <span className={`text-2xl font-bold font-mono ${colorClass}`} data-testid="wizard-total-percentage">
                              {total}%
                            </span>
                            {!isExact && (
                              <span className="text-xs text-slate-500">
                                {total > 100 ? `(+${total - 100}% sobra)` : `(${100 - total}% falta)`}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={normalizePercentages}
                      className="border-cyan-500/40 bg-cyan-900/20 text-cyan-300 hover:bg-cyan-800/40"
                      data-testid="wizard-normalize-btn"
                      title="Escala todos los porcentajes proporcionalmente para que sumen 100%"
                    >
                      Normalizar a 100%
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 text-center -mt-3">
                    💡 Ajusta cada género libremente. El total no necesita ser 100% exacto; el sistema lo normaliza al generar.
                  </p>
                </div>
              )}
              
              {/* STEP 3: Generation Config */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  {/* Modo exclusivo: SOLO playlists de Spotify del cliente */}
                  <div className="p-3 bg-emerald-900/30 border border-emerald-500/40 rounded-lg space-y-3" data-testid="wizard-modo-exclusivo-spotify">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="modo-exclusivo-spotify"
                        checked={modoExclusivoSpotify}
                        onCheckedChange={(checked) => setModoExclusivoSpotify(!!checked)}
                        data-testid="wizard-modo-exclusivo-checkbox"
                      />
                      <div className="flex-1">
                        <Label htmlFor="modo-exclusivo-spotify" className="cursor-pointer text-emerald-200 font-medium flex items-center gap-2">
                          <Music className="w-4 h-4 text-emerald-400" />
                          Crear SOLO con playlists de Spotify del cliente
                        </Label>
                        <p className="text-xs text-slate-400 mt-1">
                          Construye la playlist EXCLUSIVAMENTE con las canciones de las playlists que pegues abajo (une varias y quita duplicados). Ignora géneros, biblioteca y tendencias. Las canciones nuevas se añaden como pendientes de descargar y se clasifican con IA en tus estilos existentes.
                        </p>
                      </div>
                    </div>
                    {modoExclusivoSpotify && (
                      <div className="space-y-3" data-testid="wizard-modo-exclusivo-selector">
                        {clientSpotifyPlaylists.length > 0 ? (
                          <div>
                            <Label className="text-slate-300 text-sm">
                              Playlists Spotify del cliente ({clientSpotifyPlaylists.length})
                            </Label>
                            <div className="mt-1 space-y-1 max-h-48 overflow-auto rounded border border-slate-700 bg-slate-800/40 p-2">
                              {clientSpotifyPlaylists.map((pl, idx) => {
                                const checked = selectedClientPlaylistUrls.has(pl.url);
                                return (
                                  <label
                                    key={pl.url}
                                    className="flex items-start gap-2 cursor-pointer hover:bg-slate-700/40 rounded px-1 py-1"
                                    data-testid={`wizard-client-playlist-${idx}`}
                                  >
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={(v) => {
                                        setSelectedClientPlaylistUrls((prev) => {
                                          const next = new Set(prev);
                                          if (v) next.add(pl.url);
                                          else next.delete(pl.url);
                                          return next;
                                        });
                                      }}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm text-slate-200 truncate">{pl.label}</div>
                                      <div className="text-[10px] text-slate-500 truncate">{pl.url}</div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <button
                                type="button"
                                className="text-[11px] text-cyan-400 hover:text-cyan-300 underline"
                                onClick={() => setSelectedClientPlaylistUrls(
                                  new Set(clientSpotifyPlaylists.map((p) => p.url))
                                )}
                                data-testid="wizard-select-all-playlists"
                              >
                                Seleccionar todas
                              </button>
                              <button
                                type="button"
                                className="text-[11px] text-slate-400 hover:text-slate-200 underline"
                                onClick={() => setSelectedClientPlaylistUrls(new Set())}
                                data-testid="wizard-deselect-all-playlists"
                              >
                                Deseleccionar todas
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 italic">
                            El evento no tiene playlists guardadas. Pega URLs abajo.
                          </p>
                        )}
                        {/* Textarea plegable con URLs adicionales */}
                        <button
                          type="button"
                          className="text-xs text-emerald-300 hover:text-emerald-200 underline"
                          onClick={() => setShowExtraUrlsInput((v) => !v)}
                          data-testid="wizard-toggle-extra-urls"
                        >
                          {showExtraUrlsInput ? '− Ocultar URLs adicionales' : '+ Añadir URLs adicionales'}
                        </button>
                        {showExtraUrlsInput && (
                          <div>
                            <Textarea
                              value={spotifyUrlsExclusivo}
                              onChange={(e) => setSpotifyUrlsExclusivo(e.target.value)}
                              placeholder={"https://open.spotify.com/playlist/...\nhttps://open.spotify.com/playlist/..."}
                              rows={3}
                              className="mt-1 bg-slate-800 border-slate-600 text-slate-200 text-sm"
                              data-testid="wizard-modo-exclusivo-urls"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">
                              Se unirán con las playlists marcadas arriba.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {modoExclusivoSpotify ? (
                    <div className="text-xs text-amber-300 bg-amber-900/20 rounded p-2 border border-amber-500/30" data-testid="wizard-modo-exclusivo-aviso">
                      ⚠️ Modo exclusivo activo: las opciones de abajo (nº canciones, biblioteca, tendencias, favoritas) se ignoran.
                    </div>
                  ) : null}

                  <div className={modoExclusivoSpotify ? 'opacity-40 pointer-events-none space-y-6' : 'space-y-6'}>
                  {/* Iter88: checkbox auto-cálculo de nº canciones según duración */}
                  <div className="flex items-start space-x-3 p-3 bg-purple-900/30 border border-purple-500/40 rounded-lg">
                    <Checkbox
                      id="auto-calcular"
                      checked={config.auto_calcular_num_canciones}
                      onCheckedChange={(checked) => setConfig({...config, auto_calcular_num_canciones: checked})}
                      data-testid="wizard-auto-calc-canciones"
                    />
                    <div className="flex-1">
                      <Label htmlFor="auto-calcular" className="cursor-pointer text-slate-200 font-medium">
                        Auto-calcular nº canciones según duración del baile
                      </Label>
                      <p className="text-xs text-slate-400 mt-1">
                        Usa <code className="text-purple-300">duracion_baile</code> del evento + ratio DJ pro (~22 canciones/h) para fijar el total. Si está activo, ignora el slider de abajo.
                      </p>
                    </div>
                  </div>
                  
                  <div className={config.auto_calcular_num_canciones ? 'opacity-50 pointer-events-none' : ''}>
                    <Label className="text-slate-300">Número de Canciones (manual)</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[config.num_canciones]}
                        onValueChange={([v]) => setConfig({...config, num_canciones: v})}
                        min={20}
                        max={150}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-cyan-400 font-mono w-12 text-right">
                        {config.num_canciones}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Duración aproximada: {Math.round(config.num_canciones * 3.5)} minutos 
                      ({Math.round(config.num_canciones * 3.5 / 60 * 10) / 10} horas)
                    </p>
                  </div>
                  
                  {/* Iter88: integración playlist Spotify cliente */}
                  <div className="p-3 bg-cyan-900/30 border border-cyan-500/40 rounded-lg space-y-3">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="usar-spotify-cliente"
                        checked={config.usar_playlist_spotify_cliente}
                        onCheckedChange={(checked) => setConfig({...config, usar_playlist_spotify_cliente: checked})}
                        data-testid="wizard-usar-spotify-cliente"
                      />
                      <div className="flex-1">
                        <Label htmlFor="usar-spotify-cliente" className="cursor-pointer text-slate-200 font-medium">
                          Integrar playlist Spotify del cliente
                        </Label>
                        <p className="text-xs text-slate-400 mt-1">
                          Si el cliente ha pegado una URL de playlist Spotify, el wizard la cruzará con tu biblioteca (Denon-first) y usará LLM para sugerir afines. Requiere tu Spotify conectado en Ajustes.
                        </p>
                      </div>
                    </div>
                    {/* Feb-2026: cuando hay >1 playlist, permitir elegir cuáles usar */}
                    {config.usar_playlist_spotify_cliente && clientSpotifyPlaylists.length > 1 && (
                      <div className="pl-8 space-y-1" data-testid="wizard-normal-playlist-selector">
                        <Label className="text-slate-300 text-xs">
                          Elige qué playlist(s) usar ({clientSpotifyPlaylists.length} disponibles)
                        </Label>
                        <div className="space-y-1 max-h-40 overflow-auto rounded border border-slate-700 bg-slate-800/40 p-2">
                          {clientSpotifyPlaylists.map((pl, idx) => {
                            const checked = selectedClientPlaylistUrls.has(pl.url);
                            return (
                              <label
                                key={pl.url}
                                className="flex items-start gap-2 cursor-pointer hover:bg-slate-700/40 rounded px-1 py-1"
                                data-testid={`wizard-normal-client-playlist-${idx}`}
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    setSelectedClientPlaylistUrls((prev) => {
                                      const next = new Set(prev);
                                      if (v) next.add(pl.url);
                                      else next.delete(pl.url);
                                      return next;
                                    });
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-slate-200 truncate">{pl.label}</div>
                                  <div className="text-[10px] text-slate-500 truncate">{pl.url}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <Label className="text-slate-300">Fuentes de Canciones</Label>
                    
                    <div className="flex items-center space-x-3 p-3 bg-slate-800 rounded-lg">
                      <Checkbox
                        id="biblioteca"
                        checked={config.usar_biblioteca}
                        onCheckedChange={(checked) => setConfig({...config, usar_biblioteca: checked})}
                      />
                      <Label htmlFor="biblioteca" className="flex items-center gap-2 cursor-pointer text-slate-300">
                        <Library className="w-4 h-4 text-cyan-400" />
                        Mi Biblioteca
                        <span className="text-xs text-slate-500">(Canciones que ya tienes)</span>
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 bg-slate-800 rounded-lg">
                      <Checkbox
                        id="tendencias"
                        checked={config.usar_tendencias}
                        onCheckedChange={(checked) => setConfig({...config, usar_tendencias: checked})}
                      />
                      <Label htmlFor="tendencias" className="flex items-center gap-2 cursor-pointer text-slate-300">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        Tendencias
                        <span className="text-xs text-slate-500">(Añadir novedades de Spotify)</span>
                      </Label>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <Label className="text-slate-300">Filtros Adicionales</Label>
                    
                    <div className="flex items-center space-x-3 p-3 bg-slate-800 rounded-lg">
                      <Checkbox
                        id="favoritas"
                        checked={config.priorizar_favoritas}
                        onCheckedChange={(checked) => setConfig({...config, priorizar_favoritas: checked})}
                      />
                      <Label htmlFor="favoritas" className="flex items-center gap-2 cursor-pointer text-slate-300">
                        <Star className="w-4 h-4 text-yellow-400" />
                        Priorizar Favoritas
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 bg-slate-800 rounded-lg">
                      <Checkbox
                        id="solo5estrellas"
                        checked={config.solo_favoritas_5_estrellas}
                        onCheckedChange={(checked) => setConfig({...config, solo_favoritas_5_estrellas: checked})}
                      />
                      <Label htmlFor="solo5estrellas" className="flex items-center gap-2 cursor-pointer text-slate-300">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        Solo favoritas o 5 estrellas
                      </Label>
                    </div>
                  </div>
                  </div>
                </div>
              )}
              
              {/* STEP 4: Review */}
              {currentStep === 4 && playlistGenerada && (
                <div className="space-y-6">
                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-cyan-400">
                        {playlistGenerada.total_canciones}
                      </div>
                      <div className="text-xs text-slate-500">Canciones</div>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-green-400">
                        {Math.round((playlistGenerada.duracion_total_minutos || 0) / 60 * 10) / 10}h
                      </div>
                      <div className="text-xs text-slate-500">Duración</div>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-yellow-400">
                        {playlistGenerada.canciones_de_biblioteca}
                      </div>
                      <div className="text-xs text-slate-500">Biblioteca</div>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-pink-400">
                        {playlistGenerada.canciones_imprescindibles || 0}
                      </div>
                      <div className="text-xs text-slate-500">Cliente</div>
                    </div>
                  </div>
                  
                  {/* Info de ordenación */}
                  {playlistGenerada.orden_info && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-cyan-300">
                        <Music className="w-4 h-4" />
                        <span>{playlistGenerada.orden_info}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Iter88: Panel de información del pipeline Spotify cliente */}
                  {playlistGenerada.spotify_pipeline && (playlistGenerada.spotify_pipeline.tracks_cliente_total > 0 || playlistGenerada.spotify_pipeline.aviso || playlistGenerada.canciones_salvavidas_spotify > 0 || playlistGenerada.canciones_pendientes_denon > 0) && (
                    <div className="bg-purple-900/20 border border-purple-500/40 rounded-lg p-3 space-y-2" data-testid="wizard-spotify-pipeline-info">
                      <div className="flex items-center gap-2 text-sm font-semibold text-purple-300">
                        <Music className="w-4 h-4" />
                        Pipeline Spotify cliente / Salvavidas
                      </div>
                      {playlistGenerada.spotify_pipeline.aviso && (
                        <div className="text-xs text-amber-300 bg-amber-900/20 rounded p-2">
                          ⚠️ {playlistGenerada.spotify_pipeline.aviso}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
                        {playlistGenerada.spotify_pipeline.tracks_cliente_total > 0 && (
                          <>
                            <div>📥 Tracks playlist cliente: <span className="text-white font-semibold">{playlistGenerada.spotify_pipeline.tracks_cliente_total}</span></div>
                            <div>✅ En tu biblioteca: <span className="text-green-300 font-semibold">{playlistGenerada.spotify_pipeline.tracks_cliente_en_biblioteca}</span></div>
                            <div>⚡ LLM afines validados: <span className="text-cyan-300 font-semibold">{playlistGenerada.spotify_pipeline.tracks_recs_total}</span></div>
                            <div>🎯 Afines en biblioteca: <span className="text-green-300 font-semibold">{playlistGenerada.spotify_pipeline.tracks_recs_en_biblioteca}</span></div>
                          </>
                        )}
                        {playlistGenerada.spotify_pipeline.tracks_filtrados_por_prohibidas > 0 && (
                          <div className="col-span-2">🚫 Filtrados por prohibidas: <span className="text-red-300 font-semibold">{playlistGenerada.spotify_pipeline.tracks_filtrados_por_prohibidas}</span></div>
                        )}
                        {playlistGenerada.canciones_salvavidas_spotify > 0 && (
                          <div className="col-span-2">🛟 Salvavidas Spotify usadas: <span className="text-orange-300 font-semibold">{playlistGenerada.canciones_salvavidas_spotify}</span> <span className="text-slate-400">(no estaban en biblioteca, completan déficit)</span></div>
                        )}
                        {playlistGenerada.canciones_pendientes_denon > 0 && (
                          <div className="col-span-2">📋 Pendientes de meter en Denon: <span className="text-yellow-300 font-semibold">{playlistGenerada.canciones_pendientes_denon}</span></div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Claridad: canciones que faltan por descargar (pendiente Denon) */}
                  {(() => {
                    const pendientes = (playlistGenerada.canciones || []).filter(s => s.nueva_pendiente || s.pendiente_denon);
                    if (pendientes.length === 0) return null;
                    return (
                      <div className="bg-amber-900/15 border border-amber-500/40 rounded-lg p-3" data-testid="wizard-pendientes-descarga">
                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-200 mb-2">
                          <Download className="w-4 h-4" />
                          Canciones que te faltan por descargar ({pendientes.length})
                        </div>
                        <p className="text-xs text-slate-400 mb-2">
                          Estas NO están en tu biblioteca. Se guardarán como "pendiente Denon" para que decidas cuáles bajar según su interés.
                        </p>
                        <ScrollArea className="max-h-40">
                          <div className="space-y-1">
                            {pendientes.map((s, i) => (
                              <div key={s.id || i} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-amber-500/10 last:border-0">
                                <span className="text-slate-200 truncate flex-1">{s.title} <span className="text-slate-500">— {s.artist}</span></span>
                                <span className="text-amber-300/80 whitespace-nowrap flex-shrink-0">{s.genero?.slice(0, 12) || ''}</span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => noDescargarCancion(s)}
                                    className="px-1.5 py-0.5 rounded text-slate-400 hover:bg-cyan-500/20 hover:text-cyan-300"
                                    title="Dejar en la playlist pero NO descargar (streaming)"
                                    data-testid={`wizard-pend-nodl-${s.id || i}`}
                                  >
                                    🚫⬇️
                                  </button>
                                  <button
                                    onClick={() => quitarCancionPendiente(s)}
                                    className="px-1.5 py-0.5 rounded text-slate-400 hover:bg-red-500/20 hover:text-red-300"
                                    title="Quitar de la playlist"
                                    data-testid={`wizard-pend-remove-${s.id || i}`}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    );
                  })()}

                  {/* Resumen por género */}
                  <div>
                    <Label className="text-slate-300 mb-2 block">Distribución por Género</Label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(playlistGenerada.resumen_por_genero).map(([genero, count]) => (
                        <Badge key={genero} className="bg-slate-800 text-slate-300">
                          {genero}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Warnings */}
                  {playlistGenerada.warnings?.length > 0 && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5" />
                        <div className="text-sm text-yellow-200">
                          {playlistGenerada.warnings.map((w, i) => (
                            <p key={i}>{w}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Canciones faltantes */}
                  {playlistGenerada.canciones_faltantes?.length > 0 && (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                      <div className="text-sm text-orange-200 mb-2">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        {playlistGenerada.canciones_faltantes.length} canciones de tendencias no están en tu biblioteca:
                      </div>
                      <div className="space-y-1">
                        {playlistGenerada.canciones_faltantes.slice(0, 5).map((c, i) => (
                          <div key={i} className="text-xs text-slate-400">
                            • {c.title} - {c.artist}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Song list preview */}
                  <div>
                    <Label className="text-slate-300 mb-2 block">Vista Previa (primeras 20)</Label>
                    <ScrollArea className="h-48 bg-slate-800 rounded-lg p-3">
                      {playlistGenerada.canciones.slice(0, 20).map((song, idx) => (
                        <div 
                          key={song.id}
                          className="flex items-center justify-between py-1.5 border-b border-slate-700 last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-slate-600 text-xs w-5">{idx + 1}</span>
                            <div>
                              <div className="text-sm text-white flex items-center gap-2">
                                {song.title}
                                {song.imprescindible && (
                                  <span className="text-xs px-1.5 py-0.5 bg-pink-500/20 text-pink-300 rounded">
                                    Cliente
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 flex items-center gap-2">
                                {song.artist}
                                {song.momento && (
                                  <span className="text-cyan-400">• {song.momento}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {song.favorita && <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />}
                            {song.nueva_pendiente ? (
                              <>
                                <Badge
                                  variant="outline"
                                  className="text-xs text-amber-300 border-amber-500/50 bg-amber-900/20"
                                  title="No está en tu biblioteca — pendiente de descargar en Denon"
                                  data-testid={`wizard-song-pending-${song.id || idx}`}
                                >
                                  ⬇️ Descargar
                                </Badge>
                                <button
                                  onClick={() => noDescargarCancion(song)}
                                  className="text-xs px-1.5 py-0.5 rounded text-slate-400 hover:bg-cyan-500/20 hover:text-cyan-300"
                                  title="Dejar en la playlist pero NO descargar (la reproduces por streaming)"
                                  data-testid={`wizard-song-nodl-${song.id || idx}`}
                                >
                                  🚫⬇️
                                </button>
                                <button
                                  onClick={() => quitarCancionPendiente(song)}
                                  className="text-xs px-1.5 py-0.5 rounded text-slate-400 hover:bg-red-500/20 hover:text-red-300"
                                  title="Quitar esta canción de la playlist"
                                  data-testid={`wizard-song-remove-${song.id || idx}`}
                                >
                                  🗑️
                                </button>
                              </>
                            ) : song._no_descargar ? (
                              <Badge
                                variant="outline"
                                className="text-xs text-slate-300 border-slate-500/50 bg-slate-700/30"
                                title="Se queda en la playlist pero no la descargarás (solo streaming)"
                              >
                                ▶️ Solo streaming
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-xs text-green-300 border-green-600/50 bg-green-900/20"
                                title="Ya la tienes en tu biblioteca"
                              >
                                🎵 Biblioteca
                              </Badge>
                            )}
                            <Badge 
                              variant="outline" 
                              className="text-xs text-slate-400 border-slate-600"
                            >
                              {song.genero?.slice(0, 12) || 'Otro'}
                            </Badge>
                            {/* Iter92 Bloque C — Botón "no encaja" */}
                            <button
                              onClick={() => markNoEncaja(song)}
                              disabled={feedbackBlocked.has(song.id)}
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                feedbackBlocked.has(song.id)
                                  ? 'bg-red-700/30 text-red-500 cursor-default'
                                  : 'text-slate-500 hover:bg-red-500/20 hover:text-red-300'
                              }`}
                              title={feedbackBlocked.has(song.id)
                                ? 'Marcada como "no encaja"'
                                : 'Marcar como "no encaja" en eventos similares'}
                              data-testid={`wizard-mark-no-encaja-${song.id}`}
                            >
                              {feedbackBlocked.has(song.id) ? '✕ no encaja' : '❌'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                  
                  {/* Iter89: toggle modo de guardado (append/replace) */}
                  <div className="bg-amber-900/20 border border-amber-500/40 rounded-lg p-3 space-y-2" data-testid="wizard-save-mode">
                    <div className="text-sm font-semibold text-amber-200">Modo de guardado</div>
                    <div className="space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="save_mode"
                          value="append"
                          checked={saveMode === 'append'}
                          onChange={() => setSaveMode('append')}
                          className="mt-1"
                          data-testid="wizard-save-mode-append"
                        />
                        <div className="flex-1">
                          <div className="text-sm text-slate-200">Añadir a la playlist existente <span className="text-xs text-emerald-300">(recomendado)</span></div>
                          <div className="text-xs text-slate-400">Las canciones que ya tenía la playlist se mantienen. Solo se añaden las nuevas que no estuvieran ya (dedupe por ID).</div>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="save_mode"
                          value="replace"
                          checked={saveMode === 'replace'}
                          onChange={() => setSaveMode('replace')}
                          className="mt-1"
                          data-testid="wizard-save-mode-replace"
                        />
                        <div className="flex-1">
                          <div className="text-sm text-slate-200">Reemplazar playlist completa <span className="text-xs text-red-300">(borra las existentes)</span></div>
                          <div className="text-xs text-slate-400">⚠️ Las canciones actuales de la playlist se reemplazan por las del wizard. Úsalo solo si quieres regenerar desde cero.</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
        
        {/* Footer with navigation */}
        <div className="border-t border-slate-700 p-4 flex justify-between">
          <Button
            variant="outline"
            onClick={() => {
              if (currentStep === 1) {
                if (analyzingWithAI) {
                  const ok = window.confirm(
                    '⏳ La IA aún está analizando (~20-30s). ¿Seguro que quieres cancelar? Perderás la sugerencia.'
                  );
                  if (!ok) return;
                }
                onClose();
              } else {
                // Guardar estado al volver atrás
                saveWizardState(currentStep - 1);
                setCurrentStep(currentStep - 1);
              }
            }}
            disabled={loading || analyzingWithAI}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {currentStep === 1 ? 'Cancelar' : 'Atrás'}
          </Button>
          
          {currentStep < 4 ? (
            <div className="flex items-center gap-2">
              {/* Botón secundario: saltar IA cuando hay porcentajes manuales */}
              {currentStep === 1 && hasManualPercentages && generosSugeridos.length > 0 && (
                <Button
                  onClick={skipAIUseManual}
                  disabled={loading || analyzingWithAI}
                  variant="outline"
                  className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
                  data-testid="skip-ai-use-manual-btn"
                >
                  <ChevronRight className="w-4 h-4 mr-1" />
                  Usar mis porcentajes
                </Button>
              )}
              <Button
                onClick={() => {
                  if (currentStep === 1) {
                    getSuggestions();
                  } else if (currentStep === 2) {
                    // Guardar estado al pasar al paso 3
                    saveWizardState(3);
                    setCurrentStep(3);
                  } else if (currentStep === 3) {
                    generatePlaylist();
                  }
                }}
                disabled={loading || analyzingWithAI}
                className="bg-cyan-600 hover:bg-cyan-500"
                data-testid="wizard-next-btn"
              >
                {(loading || analyzingWithAI) ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ChevronRight className="w-4 h-4 mr-1" />
                )}
                {currentStep === 1
                  ? (analyzingWithAI ? 'Analizando…' : (hasManualPercentages ? '🪄 Pedir sugerencia IA' : 'Analizar con IA'))
                  : currentStep === 2
                    ? 'Siguiente'
                    : 'Generar Playlist'}
              </Button>
            </div>
          ) : (
            <Button
              onClick={savePlaylist}
              disabled={saving}
              className="bg-green-600 hover:bg-green-500"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Playlist
            </Button>
          )}
        </div>
      </Card>
      
      {/* Iter89.8: Dialog Editar Preset */}
      <Dialog open={!!editingPreset} onOpenChange={(open) => { if (!open) setEditingPreset(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md" data-testid="wizard-preset-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar preset</DialogTitle>
            <DialogDescription className="text-slate-400">
              Cambia el nombre y la descripción. Para editar la distribución de géneros, aplica el preset, modifica los porcentajes en la lista y luego pulsa "Guardar actual" sobreescribiendo este preset con el mismo nombre.
            </DialogDescription>
          </DialogHeader>
          {editingPreset && (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300 text-xs">Nombre</Label>
                <Input
                  value={editingPreset.label}
                  onChange={(e) => setEditingPreset({ ...editingPreset, label: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="Ej: 🌃 Boda nocturna jóvenes"
                  data-testid="wizard-preset-edit-label-input"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Descripción corta</Label>
                <Input
                  value={editingPreset.desc}
                  onChange={(e) => setEditingPreset({ ...editingPreset, desc: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="Ej: Pop + Reggaeton · alta energía"
                  data-testid="wizard-preset-edit-desc-input"
                />
              </div>
              <div className="bg-slate-800/60 rounded p-2 border border-slate-700">
                <div className="text-[10px] uppercase text-slate-500 mb-1">Distribución actual</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(editingPreset.distribution || {}).map(([g, p]) => (
                    <span key={g} className="text-[11px] px-1.5 py-0.5 bg-slate-900 border border-slate-700 rounded text-slate-300">
                      {g}: <span className="text-cyan-300 font-mono">{p}%</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingPreset(null)} className="bg-slate-800 border-slate-600 text-slate-300" data-testid="wizard-preset-edit-cancel">
              Cancelar
            </Button>
            <Button onClick={updatePreset} className="bg-cyan-600 hover:bg-cyan-500" data-testid="wizard-preset-edit-save">
              <Save className="w-4 h-4 mr-2" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Iter89.8: Dialog Guardar configuración actual como preset */}
      <Dialog open={savePresetDialog} onOpenChange={setSavePresetDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md" data-testid="wizard-save-preset-dialog">
          <DialogHeader>
            <DialogTitle>Guardar como preset personalizado</DialogTitle>
            <DialogDescription className="text-slate-400">
              La distribución actual de géneros se guardará como un preset reutilizable. Se normaliza a 100% automáticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-300 text-xs">Nombre del preset</Label>
              <Input
                value={newPresetForm.label}
                onChange={(e) => setNewPresetForm({ ...newPresetForm, label: e.target.value })}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Ej: 🎂 Mi mezcla cumpleaños 30+"
                autoFocus
                data-testid="wizard-save-preset-label-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Descripción corta (opcional)</Label>
              <Input
                value={newPresetForm.desc}
                onChange={(e) => setNewPresetForm({ ...newPresetForm, desc: e.target.value })}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Ej: Pop español + Indie + Dance"
                data-testid="wizard-save-preset-desc-input"
              />
            </div>
            <div className="bg-slate-800/60 rounded p-2 border border-slate-700">
              <div className="text-[10px] uppercase text-slate-500 mb-1">Distribución a guardar</div>
              <div className="flex flex-wrap gap-1">
                {generosSugeridos.filter(g => g.porcentaje > 0).map(g => (
                  <span key={g.genero} className="text-[11px] px-1.5 py-0.5 bg-slate-900 border border-slate-700 rounded text-slate-300">
                    {g.genero}: <span className="text-cyan-300 font-mono">{g.porcentaje}%</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSavePresetDialog(false)} className="bg-slate-800 border-slate-600 text-slate-300" data-testid="wizard-save-preset-cancel">
              Cancelar
            </Button>
            <Button onClick={confirmSaveCurrentAsPreset} className="bg-cyan-600 hover:bg-cyan-500" data-testid="wizard-save-preset-confirm">
              <Save className="w-4 h-4 mr-2" /> Guardar preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Preview Wizard IA — validación de integridad antes de aplicar */}
      <Dialog open={!!wizardIaPreview} onOpenChange={(o) => !o && setWizardIaPreview(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl" data-testid="wizard-ia-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" /> Optimización IA — Preview
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Revisa antes de aplicar. Nada se guarda hasta que confirmes.
            </DialogDescription>
          </DialogHeader>
          {wizardIaPreview && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="rounded-md bg-slate-800 p-2">
                  <div className="text-2xl font-bold text-white">{wizardIaPreview.stats.song_count}</div>
                  <div className="text-xs text-slate-400 uppercase">Canciones</div>
                </div>
                <div className="rounded-md bg-slate-800 p-2">
                  <div className="text-2xl font-bold text-red-400">{wizardIaPreview.stats.url_count}</div>
                  <div className="text-xs text-slate-400 uppercase">URLs YouTube</div>
                </div>
                <div className="rounded-md bg-slate-800 p-2">
                  <div className="text-2xl font-bold text-cyan-400">{wizardIaPreview.stats.momento_count}</div>
                  <div className="text-xs text-slate-400 uppercase">Momentos</div>
                </div>
                <div className="rounded-md bg-slate-800 p-2">
                  <div className="text-2xl font-bold text-amber-400">{wizardIaPreview.stats.chunks_of_10 || '—'}</div>
                  <div className="text-xs text-slate-400 uppercase">Chunks 10</div>
                </div>
              </div>

              {wizardIaPreview.momentos_detected.length > 0 && (
                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">Momentos detectados</div>
                  <div className="flex flex-wrap gap-1">
                    {wizardIaPreview.momentos_detected.map((m) => (
                      <Badge key={m} variant="outline" className="text-cyan-300 border-cyan-700">{m}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {wizardIaPreview.youtube_urls.length > 0 && (
                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">URLs YouTube → se moverán a Peticiones</div>
                  <ScrollArea className="max-h-32 rounded bg-slate-800 p-2 border border-slate-700">
                    {wizardIaPreview.youtube_urls.map((u, i) => (
                      <div key={i} className="text-xs text-slate-300 mb-1">
                        <span className="text-red-400 mr-1">▶</span>
                        <code className="text-slate-400">{u.video_id}</code>
                        {u.momento && <span className="ml-2 text-cyan-400">[{u.momento}]</span>}
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}

              {/* Refinamiento IA — sólo aparece si hay canciones sin artista */}
              {wizardIaPreview.songs.some((s) => !s.artist) && (
                <div className="rounded-md border border-purple-800/40 bg-purple-950/20 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase text-purple-300 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Refinar títulos ambiguos
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={runRefineWithAI}
                      disabled={refineLoading}
                      className="bg-purple-700 hover:bg-purple-600 text-white h-7 text-xs"
                      data-testid="wizard-ia-refine-btn"
                    >
                      {refineLoading ? 'Consultando IA…' : '🤖 Sugerir artistas con IA'}
                    </Button>
                  </div>
                  {refineSuggestions.length === 0 && (
                    <p className="text-xs text-slate-400">
                      {wizardIaPreview.songs.filter((s)=>!s.artist).length} canciones sin artista.
                      La IA sugerirá el más probable en chunks de 10.
                    </p>
                  )}
                  {refineSuggestions.length > 0 && (
                    <ScrollArea className="max-h-40">
                      {refineSuggestions.map((s, i) => {
                        const confColor = s.confidence === 'high' ? 'text-green-400' : s.confidence === 'medium' ? 'text-amber-400' : 'text-slate-500';
                        return (
                          <label
                            key={i}
                            className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-800/40 rounded px-1"
                            data-testid={`wizard-ia-refine-item-${i}`}
                          >
                            <input
                              type="checkbox"
                              checked={!!refineAccepted[s.title]}
                              onChange={(e) => setRefineAccepted((prev) => ({ ...prev, [s.title]: e.target.checked }))}
                              className="accent-purple-500"
                            />
                            <span className="text-xs flex-1">
                              <span className="text-white font-semibold">{s.suggested_artist}</span>
                              <span className="text-slate-500 mx-1">—</span>
                              <span className="text-slate-300">{s.title}</span>
                            </span>
                            <span className={`text-[10px] uppercase font-bold ${confColor}`}>
                              {s.confidence}
                            </span>
                          </label>
                        );
                      })}
                    </ScrollArea>
                  )}
                </div>
              )}

              {wizardIaPreview.songs.length > 0 && (
                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">Canciones limpiadas (primeras 10)</div>
                  <ScrollArea className="max-h-40 rounded bg-slate-800 p-2 border border-slate-700">
                    {wizardIaPreview.songs.slice(0, 10).map((s, i) => (
                      <div key={i} className="text-xs text-slate-300 mb-1">
                        {s.artist ? <span className="text-white font-semibold">{s.artist}</span> : null}
                        {s.artist ? <span className="text-slate-500"> — </span> : null}
                        <span>{s.title}</span>
                        {s.momento && <span className="ml-2 text-cyan-400 text-[10px] uppercase">[{s.momento}]</span>}
                      </div>
                    ))}
                    {wizardIaPreview.songs.length > 10 && (
                      <div className="text-xs text-slate-500 italic">…y {wizardIaPreview.songs.length - 10} más</div>
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWizardIaPreview(null)} data-testid="wizard-ia-cancel">
              Cancelar
            </Button>
            <Button
              onClick={applyWizardIa}
              disabled={wizardIaApplying}
              className="bg-purple-600 hover:bg-purple-500 text-white"
              data-testid="wizard-ia-confirm"
            >
              {wizardIaApplying ? 'Aplicando…' : `Aplicar (${wizardIaPreview?.stats?.url_count || 0} URLs + ${wizardIaPreview?.stats?.song_count || 0} canciones)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventWizard;
